import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCreateSectionLoading, selectIsOpenSaveSection } from '@/store/section/selectors';
import { setIsOpenSaveSection } from '@/store/section/slice';
import { useCopyPasteHelper } from '@/hooks/useCopyPasteHelper';
import { createSection } from '@/store/section/thunks';
import { useEditor } from '@craftjs/core';

import IconSpinner from '@/assets/icons/icon-spiner.svg?react';
import IconCheckmarkCircleFill from '@/assets/icons/icon-checkmark-circle-fill.svg?react';
import { SECTION_CATEGORIES, POPUP_NODE_NAME } from '@builder4/shared/utils/constants';
import { useMixpanel } from '@/hooks/useMixpanel';
import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';

interface SaveSectionProps {
  className?: string;
  onSave?: (data: { name: string; category: string }) => void;
  initialName?: string;
  initialCategory?: string;
  categories?: { value: string; label: string }[];
  onClose?: () => void;
  phase?: 'idle' | 'saving' | 'saved';
  onSaved?: () => void;
}

export const SaveSection: React.FC<SaveSectionProps> = ({
  className = '',
  onSave,
  initialName = 'New Section',
  categories = Object.values(SECTION_CATEGORIES),
  onClose,
}) => {
  const { selected, isPopup } = useEditor((_, query) => {
    const selectedId = query.getEvent('selected').first();
    const node = selectedId ? query.node(selectedId).get() : null;
    return {
      selected: selectedId,
      isPopup: node?.data.displayName === POPUP_NODE_NAME,
    };
  });

  const { getSerializedNodeTreeToSave } = useCopyPasteHelper();

  const defaultCategory = isPopup ? SECTION_CATEGORIES.popup.value : categories[0]?.value;
  const [name, setName] = useState(initialName);
  const [category, setCategory] = useState(defaultCategory);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const isOpen = useAppSelector(selectIsOpenSaveSection);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setCategory(defaultCategory);
      setPhase('idle');
    }
  }, [isOpen]);
  const saving = useAppSelector(selectCreateSectionLoading);
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'idle' | 'saving' | 'saved'>('idle');
  const savedTimeoutRef = useRef<number | null>(null);

  const isSaving = saving || phase === 'saving';
  const buttonState: 'default' | 'disabled' | 'loading' = isSaving
    ? 'loading'
    : name.trim()
      ? 'default'
      : 'disabled';

  // Check if value is valid for saving
  const isValidValue = name.trim().length > 0;

  const headerClasses =
    'w-72 h-10 pl-3 pr-2 bg-neutral-0 rounded-tl-lg rounded-tr-lg outline outline-offset-[-1px] outline-neutral-100 inline-flex justify-between items-center';

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setIsOpenSaveSection(false));
      setName(initialName);
      setCategory(defaultCategory);
    }
  };

  const track = useMixpanel();

  const handleSave = async () => {
    if (isSaving) return;

    // Validate name before saving
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }

    setPhase('saving');
    try {
      onSave?.({ name: trimmedName, category });
      const body = {
        name: trimmedName,
        section_group_id: category,
        craft_json: getSerializedNodeTreeToSave(selected),
      };

      await dispatch(createSection(body)).unwrap();

      track(EVENT.SECTION_SAVED, {
        [PROPERTY.SOURCE]: SOURCE.ACTIONS,
      });

      setPhase('saved');
      if (savedTimeoutRef.current) window.clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = window.setTimeout(() => {
        handleClose();
        setPhase('idle');
      }, 1500);
    } catch {
      setPhase('idle');
    }
  };

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Don't close if clicking inside the modal
      if (modalRef.current && !modalRef.current.contains(target)) {
        // Don't close if the select dropdown is open and we're clicking on dropdown content
        if (isSelectOpen) {
          // Check if the click is on a dropdown element
          return;
        }
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, isSelectOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSelectOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, isSelectOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) window.clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center">
        <div
          className="absolute inset-0 bg-transparent"
          onClick={handleClose}
          data-testid="save-section-backdrop"
        />

        <div
          className="pointer-events-auto absolute top-[60px] left-1/2 -translate-x-1/2"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={modalRef}
            className={clsx(
              'bg-neutral-0 fixed top-16 left-1/2 z-[10000] inline-flex w-72 -translate-x-1/2 transform flex-col items-center justify-start gap-3 rounded-lg px-3 pb-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-offset-[-0.8px] outline-neutral-100 select-none',
              className,
            )}
          >
            <div className={headerClasses}>
              <div className="flex flex-1 items-center gap-2 border-b border-transparent py-1">
                <div className="text-button-sm leading-none font-medium text-neutral-900">
                  Save Section
                </div>
              </div>
              <div className="inline-flex h-6 w-6 flex-col items-center justify-start">
                <IconSimpleButton
                  size={24}
                  icon={IconClose}
                  onClick={handleClose}
                  data-qa-selector="save-section-close-button"
                />
              </div>
            </div>

            <div className="flex flex-col items-start justify-start gap-3 self-stretch pt-2 pb-1">
              {/* Name Field */}
              <div className="inline-flex h-8 items-center justify-between self-stretch">
                <TextField
                  className="max-w-full flex-1"
                  classNameInputParent="w-40"
                  classNameInput="bg-neutral-50"
                  text="Name"
                  inputValue={name}
                  placeholderText="New Section"
                  onChange={(e) => setName(e.target.value)}
                  data-qa-selector="save-section-name-input"
                />
              </div>

              {/* Category Select */}
              <div className="inline-flex h-8 items-center justify-between self-stretch">
                <p className="text-button-sm font-medium text-neutral-500">Category</p>
                <div className="w-40">
                  <Select
                    value={category}
                    onValueChange={setCategory}
                    onOpenChange={setIsSelectOpen}
                  >
                    <SelectTrigger
                      size="default"
                      className="w-40"
                      aria-label="Category"
                      data-qa-selector="save-section-category-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Save Button with phases */}
              {!isSaving && phase !== 'saved' && (
                <PrimaryButton
                  icon={null}
                  size="sm"
                  state={isValidValue ? 'default' : 'disabled'}
                  text="Save"
                  className="h-7 w-full"
                  onClick={() => {
                    if (buttonState === 'default') handleSave();
                  }}
                  data-qa-selector="save-section-save-button"
                />
              )}
              {isSaving && (
                <PrimaryButton
                  icon={IconSpinner}
                  size="sm"
                  state="loading"
                  text="Saving..."
                  className="h-7 w-full"
                  disabled
                  data-qa-selector="save-section-save-button"
                />
              )}
              {!isSaving && phase === 'saved' && (
                <PrimaryButton
                  icon={IconCheckmarkCircleFill}
                  size="sm"
                  state="default"
                  text="Saved"
                  className="h-7 w-full"
                  disabled
                  data-qa-selector="save-section-save-button"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export type { SaveSectionProps };
